import React, { useState, useMemo } from 'react';
import { useGame } from '../context/GameContext';
import GameCard from '../components/GameCard';
import { CARD_POOL_COMMON, CARD_POOL_UNCOMMON, CARD_POOL_RARE } from '../game/cards';
import { CLASS_LIST, CLASSES } from '../game/classes';
import { makeRng, genSeed } from '../game/rng';
import { ArrowLeft, Play, Shuffle, Check } from 'lucide-react';

const DRAFT_POOL_SIZE = 20;
const DRAFT_PICK_COUNT = 10;

export default function Draft() {
  const [seed] = useState(() => genSeed());
  const [chosenClass, setChosenClass] = useState('sigma');
  const [picked, setPicked] = useState([]);
  const { setDraftDeck, beginClassSelectWithDraft, goToMenu } = useGame();

  // Deterministic pool from seed
  const pool = useMemo(() => {
    const rng = makeRng(seed + '-draft');
    const combined = [
      ...CARD_POOL_COMMON, ...CARD_POOL_COMMON,
      ...CARD_POOL_UNCOMMON, ...CARD_POOL_RARE,
    ];
    const shuffled = [...combined].sort(() => rng() - 0.5);
    return shuffled.slice(0, DRAFT_POOL_SIZE);
  }, [seed]);

  const togglePick = (idx) => {
    setPicked(prev => {
      if (prev.includes(idx)) return prev.filter(i => i !== idx);
      if (prev.length >= DRAFT_PICK_COUNT) return prev;
      return [...prev, idx];
    });
  };

  const confirmDraft = () => {
    // Build a draft deck combining picks + a few basic yap/cope for consistency
    const baseFillers = ['yap','yap','cope','cope','cope'];
    const draftedCards = picked.map(i => pool[i]);
    const finalDeck = [...baseFillers, ...draftedCards];
    setDraftDeck(finalDeck);
    beginClassSelectWithDraft(chosenClass, seed);
  };

  const remaining = DRAFT_PICK_COUNT - picked.length;

  return (
    <div className="min-h-screen w-full px-4 md:px-6 py-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <button className="brutal-btn text-sm" onClick={goToMenu} data-testid="draft-back-button">
          <ArrowLeft size={16} /> BACK
        </button>
        <h1 className="font-display text-3xl sm:text-5xl font-black uppercase text-[color:var(--neon)]">DRAFT MODE</h1>
        <div className="text-xs uppercase tracking-widest text-[color:var(--text-dim)]">SEED: <span className="text-[color:var(--pink)]">{seed}</span></div>
      </div>

      <div className="mt-4 text-center text-sm uppercase tracking-widest text-[color:var(--text-dim)]">
        pick <span className="text-[color:var(--pink)] font-bold">{DRAFT_PICK_COUNT}</span> from <span className="text-[color:var(--neon)] font-bold">{DRAFT_POOL_SIZE}</span> — deck is fixed for the whole run
      </div>

      {/* Class picker */}
      <div className="mt-6 grid grid-cols-3 gap-3 max-w-3xl mx-auto">
        {CLASS_LIST.map(id => {
          const c = CLASSES[id];
          const active = chosenClass === id;
          return (
            <button
              key={id}
              className="p-3 border-2 transition-all"
              style={{ borderColor: active ? c.color : 'var(--muted)', boxShadow: active ? `4px 4px 0 ${c.color}` : 'none' }}
              onClick={() => setChosenClass(id)}
              data-testid={`draft-class-${id}`}
            >
              <div className="text-3xl">{c.icon}</div>
              <div className="font-display font-black uppercase text-xs mt-1" style={{color: c.color}}>{c.name}</div>
            </button>
          );
        })}
      </div>

      {/* Progress */}
      <div className="mt-6 max-w-4xl mx-auto brutal-border p-3 flex items-center justify-between">
        <div className="text-xs uppercase tracking-widest text-[color:var(--text-dim)]">PICKS</div>
        <div className="flex-1 mx-4 h-3 bg-black border border-[color:var(--muted)]">
          <div className="h-full bg-[color:var(--neon)] transition-all" style={{width: `${(picked.length / DRAFT_PICK_COUNT) * 100}%`}} />
        </div>
        <div className="font-display font-black text-[color:var(--neon)]" data-testid="draft-picks-count">{picked.length}/{DRAFT_PICK_COUNT}</div>
      </div>

      {/* Card grid */}
      <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 gap-3 max-w-6xl mx-auto" data-testid="draft-card-pool">
        {pool.map((cid, i) => {
          const selected = picked.includes(i);
          return (
            <div key={i} className="flex flex-col items-center gap-1 relative">
              <div onClick={() => togglePick(i)} className={selected ? 'selected' : ''} style={{cursor: 'pointer'}}>
                <GameCard cardId={cid} testid={`draft-card-${i}`} selected={selected} />
              </div>
              {selected && (
                <div className="absolute top-1 left-1 z-20 bg-[color:var(--pink)] text-black w-6 h-6 grid place-items-center font-black text-xs border-2 border-black">
                  <Check size={12}/>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-8 flex justify-center gap-4 pb-10">
        <button
          className="brutal-btn text-lg"
          onClick={confirmDraft}
          disabled={picked.length !== DRAFT_PICK_COUNT}
          data-testid="draft-confirm-button"
        >
          <Play size={18}/> DROP IN WITH DRAFT · {remaining} LEFT
        </button>
      </div>
    </div>
  );
}
